import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tip } from "@/components/ds/Tip";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Building,
  Share2,
  // ShoppingCart, // re-importar al reactivar los CTAs de suscripción
  Smartphone,
  TrendingUp,
  Users,
} from "@/components/ds/icons";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui/PageHeader";

interface UsuarioResumen {
  id: number;
  name: string;
  cargo: string | null;
}

interface MandantePorOrigen {
  empresa: { id: number; nombre: string };
  total: number;
  usadas: number;
  usuarios: UsuarioResumen[];
}

interface ContratistaPorDestino {
  empresa: { id: number; nombre: string } | null;
  total: number;
  usadas: number;
}

interface MandanteVinculado {
  id: number;
  nombre: string;
  estado_relacion: "pendiente" | "activa" | "rechazada" | "desvinculada";
}

interface LicenciaResumen {
  propias: {
    total: number;
    usadas: number;
    cedidas: number;
    disponibles: number;
    usuarios: UsuarioResumen[];
  };
  mandantes: {
    total: number;
    usadas: number;
    por_origen: MandantePorOrigen[];
  };
  cedidas: {
    total: number;
    usadas: number;
    disponibles: number;
    por_destino: ContratistaPorDestino[];
  };
  mandantes_vinculados: MandanteVinculado[];
  tengo_contratistas: boolean;
}

export function ResumenCupos() {
  const [resumen, setResumen] = useState<LicenciaResumen | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    api
      .get<{ data: LicenciaResumen }>("/me/licencias/resumen")
      .then((res) => {
        if (!cancelled) setResumen(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError) {
          toast.error(err.message || "No se pudo cargar el resumen de licencias");
        } else {
          toast.error("No se pudo cargar el resumen de licencias");
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (isLoading) {
    return <ResumenSkeleton />;
  }

  if (!resumen) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Resumen de Licencias"
          subtitle="Cuántas licencias tienes, de dónde vienen y cuántas quedan libres."
          // cta={{ label: "Comprar Más Licencias", icon: ShoppingCart, href: "/configuracion/suscripcion" }} — oculto hasta activar suscripciones
        />
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No se pudieron cargar los datos. Intenta recargar la página.
          </CardContent>
        </Card>
      </div>
    );
  }

  const { propias, mandantes, cedidas, mandantes_vinculados, tengo_contratistas } = resumen;

  const totalUsadas = propias.usadas + mandantes.usadas;
  const propiasParaMi = Math.max(0, propias.total - propias.cedidas);
  const totalCapacidad = propiasParaMi + mandantes.total;
  const disponiblesParaMi = Math.max(0, totalCapacidad - totalUsadas);
  const tasaUso = totalCapacidad > 0 ? Math.round((totalUsadas / totalCapacidad) * 100) : 0;
  const propiasPorcentaje = propias.total > 0 ? (propias.usadas / propias.total) * 100 : 0;
  const mandantesPorcentaje = mandantes.total > 0 ? (mandantes.usadas / mandantes.total) * 100 : 0;
  const cedidasPorcentaje = cedidas.total > 0 ? (cedidas.usadas / cedidas.total) * 100 : 0;
  const sinDatos =
    propias.total === 0 && mandantes.total === 0 && cedidas.total === 0;

  if (sinDatos && mandantes_vinculados.length === 0 && !tengo_contratistas) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Resumen de Licencias"
          subtitle="Cuántas licencias tienes, de dónde vienen y cuántas quedan libres."
          // cta={{ label: "Comprar Más Licencias", icon: ShoppingCart, href: "/configuracion/suscripcion" }} — oculto hasta activar suscripciones
        />
        <Card>
          <CardContent className="py-12 text-center space-y-4">
            <p className="text-muted-foreground">
              Aún no tienes licencias asignadas a tu empresa.
            </p>
            {/* CTA "Ver Planes" → /configuracion/suscripcion oculto hasta activar suscripciones */}
            {/* <Link href="/configuracion/suscripcion">
              <Button className="gap-2">
                <ShoppingCart className="h-4 w-4" />
                Ver Planes
              </Button>
            </Link> */}
          </CardContent>
        </Card>
      </div>
    );
  }

  const esContratista = mandantes_vinculados.length > 0;
  const tieneCesionesMandante = mandantes.por_origen.length > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Resumen de Licencias"
        subtitle="Cuántas licencias tienes, de dónde vienen y cuántas quedan libres."
        // cta={{ label: "Comprar Más Licencias", icon: ShoppingCart, href: "/configuracion/suscripcion" }} — oculto hasta activar suscripciones
      />

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total Licencias Activas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold">{totalUsadas}</div>
            <p className="text-sm text-muted-foreground mt-1">de {totalCapacidad} disponibles</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Licencias Disponibles</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-success">{disponiblesParaMi}</div>
            <p className="text-sm text-muted-foreground mt-1">Sin asignar</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Tasa de Uso</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold">{tasaUso}%</div>
            <p className="text-sm text-muted-foreground mt-1">Capacidad utilizada</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Smartphone className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle>Licencias Propias</CardTitle>
                  <CardDescription>Pagadas por mi empresa</CardDescription>
                </div>
              </div>
              <Badge variant="default" className="text-lg px-3 py-1">
                {propias.usadas} / {propias.total}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Utilizadas por mis trabajadores</span>
                <span className="font-medium">{propias.usadas}</span>
              </div>
              <Progress value={propiasPorcentaje} className="h-3" />
              {propias.cedidas > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Cedidas a contratistas</span>
                  <span className="font-medium text-info">{propias.cedidas}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Libres para asignar</span>
                <span className="font-medium text-success">{propias.disponibles}</span>
              </div>
            </div>

            {/* Callout de upsell "¿Necesitas más licencias? → Ver Planes" oculto
                hasta activar suscripciones (toda la caja es un CTA de compra). */}
            {/* <div className="rounded-lg border bg-muted/50 p-4">
              <div className="flex items-start gap-3">
                <TrendingUp className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium text-sm">¿Necesitas más licencias?</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Cambiá tu plan para agregar más usuarios y expandir tu operación.
                  </p>
                  <Link href="/configuracion/suscripcion">
                    <Button variant="outline" size="sm" className="mt-3">
                      Ver Planes
                    </Button>
                  </Link>
                </div>
              </div>
            </div> */}

            <div className="space-y-2 pt-2">
              <p className="text-sm font-medium">Usuarios Activos:</p>
              {propias.usuarios.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sin usuarios asignados.</p>
              ) : (
                <div className="space-y-1">
                  {propias.usuarios.map((u) => (
                    <div key={u.id} className="flex items-center gap-2 text-sm">
                      <div className="h-2 w-2 rounded-full bg-success-bar" />
                      <span>
                        {u.name}
                        {u.cargo ? ` — ${u.cargo}` : ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {esContratista ? (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-info-bg">
                    <Building className="h-5 w-5 text-info" />
                  </div>
                  <div>
                    <CardTitle>Licencias de Mandantes</CardTitle>
                    <CardDescription>Pagadas por empresas cliente</CardDescription>
                  </div>
                </div>
                <Badge variant="secondary" className="text-lg px-3 py-1">
                  {mandantes.usadas} / {mandantes.total}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {tieneCesionesMandante ? (
                <>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Utilizadas</span>
                      <span className="font-medium">{mandantes.usadas} licencias</span>
                    </div>
                    <Progress value={mandantesPorcentaje} className="h-3" />
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Disponibles</span>
                      <span className="font-medium text-success">
                        {mandantes.total - mandantes.usadas} licencias
                      </span>
                    </div>
                  </div>

                  <div className="rounded-lg border bg-info-bg p-4">
                    <div className="flex items-start gap-3">
                      <Users className="h-5 w-5 text-info mt-0.5" />
                      <div>
                        <p className="font-medium text-sm">Licencias compartidas</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Tus mandantes te cedieron licencias para tus trabajadores.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 pt-2">
                    <p className="text-sm font-medium">Desglose por Mandante:</p>
                    {mandantes.por_origen.map((item) => (
                      <div key={item.empresa.id} className="rounded-lg border p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{item.empresa.nombre}</span>
                          <Badge variant="outline">
                            {item.usadas}/{item.total}
                          </Badge>
                        </div>
                        <Progress
                          value={item.total > 0 ? (item.usadas / item.total) * 100 : 0}
                          className="h-2"
                        />
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="rounded-lg border border-dashed bg-muted/30 p-4 space-y-2">
                  <p className="text-sm font-medium">Aún no recibiste cesiones.</p>
                  <p className="text-sm text-muted-foreground">
                    {mandantes_vinculados.length === 1 ? (
                      <>
                        Pedile a <strong>{mandantes_vinculados[0].nombre}</strong> que te entregue
                        licencias desde su panel de contratistas.
                      </>
                    ) : (
                      <>
                        Tus mandantes (
                        {mandantes_vinculados.map((m, i) => (
                          <span key={m.id}>
                            {i > 0 ? ", " : ""}
                            <strong>{m.nombre}</strong>
                          </span>
                        ))}
                        ) pueden cederte licencias desde su panel de contratistas.
                      </>
                    )}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-dashed">
            <CardContent className="py-6 space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                  <Building className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium text-sm">¿Trabajás como contratista?</p>
                  <p className="text-xs text-muted-foreground">
                    Tu mandante puede cederte licencias para tus trabajadores.
                  </p>
                </div>
              </div>
              <Link href="/comercial/mandantes">
                <Button variant="outline" size="sm" className="gap-2">
                  <Building className="h-4 w-4" />
                  Vincular un Mandante
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>

      {tengo_contratistas && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-warning-bg">
                  <Share2 className="h-5 w-5 text-warning" />
                </div>
                <div>
                  <CardTitle>Licencias cedidas a contratistas</CardTitle>
                  <CardDescription>
                    Licencias de tu suscripción entregadas a empresas que trabajan bajo tu cargo
                  </CardDescription>
                </div>
              </div>
              <Badge variant="secondary" className="text-lg px-3 py-1">
                {cedidas.usadas} / {cedidas.total}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {cedidas.total === 0 ? (
              <div className="rounded-lg border border-dashed bg-muted/30 p-4 space-y-2">
                <p className="text-sm font-medium">Todavía no cediste licencias.</p>
                <p className="text-sm text-muted-foreground">
                  Puedes ceder licencias a tus contratistas desde el detalle de cada uno.
                </p>
                <Link href="/comercial/contratistas">
                  <Button variant="outline" size="sm" className="mt-2 gap-2">
                    <Building className="h-4 w-4" />
                    Ir a Contratistas
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">En uso por contratistas</span>
                    <span className="font-medium">{cedidas.usadas} licencias</span>
                  </div>
                  <Progress value={cedidasPorcentaje} className="h-3" />
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Cedidas y libres</span>
                    <span className="font-medium text-success">
                      {cedidas.disponibles} licencias
                    </span>
                  </div>
                </div>

                <div className="space-y-3 pt-2">
                  <p className="text-sm font-medium">Desglose por contratista:</p>
                  <div className="grid gap-3 md:grid-cols-2">
                    {cedidas.por_destino.map((item) =>
                      item.empresa ? (
                        <div
                          key={item.empresa.id}
                          className="rounded-lg border p-3 space-y-2"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{item.empresa.nombre}</span>
                            <Badge variant="outline">
                              {item.usadas}/{item.total}
                            </Badge>
                          </div>
                          <Progress
                            value={item.total > 0 ? (item.usadas / item.total) * 100 : 0}
                            className="h-2"
                          />
                        </div>
                      ) : null,
                    )}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Acciones Rápidas</CardTitle>
          <CardDescription>Gestioná tus licencias y usuarios</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <Link href="/licencias/asignacion-usuarios">
              <Button variant="outline" className="w-full justify-start gap-2">
                <Users className="h-4 w-4" />
                Asignar Licencias
              </Button>
            </Link>
            {/* Quick action "Comprar Más Licencias" → /configuracion/suscripcion oculto hasta activar suscripciones */}
            {/* <Link href="/configuracion/suscripcion">
              <Button variant="outline" className="w-full justify-start gap-2">
                <ShoppingCart className="h-4 w-4" />
                Comprar Más Licencias
              </Button>
            </Link> */}
            <Tip texto="Próximamente" claseEnvoltorio="w-full">
              <Button
                variant="outline"
                className="w-full justify-start gap-2"
                disabled
              >
                <TrendingUp className="h-4 w-4" />
                Ver Uso Histórico
              </Button>
            </Tip>
            <Link href="/comercial/mandantes">
              <Button variant="outline" className="w-full justify-start gap-2">
                <Building className="h-4 w-4" />
                Gestionar Mandantes
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ResumenSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="h-9 w-72" />
          <Skeleton className="mt-2 h-4 w-80" />
        </div>
        <Skeleton className="h-11 w-56" />
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="space-y-3 pt-6">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-10 w-20" />
              <Skeleton className="h-3 w-24" />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="space-y-4 pt-6">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
